import React from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import './Home.css';

const Home = () => {
  const adminStats = [
    { label: 'Total Classes', value: '2', tone: 'blue', icon: 'bi-easel2' },
    { label: 'Total Students', value: '22', tone: 'teal', icon: 'bi-person' },
    { label: 'Total Teachers', value: '1', tone: 'amber', icon: 'bi-people' },
    { label: 'Total Parents', value: '20', tone: 'violet', icon: 'bi-person' },
    { label: 'Total Waiting List', value: '0', tone: 'rose', icon: 'bi-clock' }
  ];

  const attendanceBars = [
    { day: 'Mon', present: 16, late: 1, absent: 2, excused: 1 },
    { day: 'Tue', present: 17, late: 2, absent: 1, excused: 1 },
    { day: 'Wed', present: 15, late: 2, absent: 3, excused: 1 },
    { day: 'Thu', present: 18, late: 1, absent: 1, excused: 1 },
    { day: 'Fri', present: 18, late: 2, absent: 2, excused: 1 },
    { day: 'Sat', present: 19, late: 1, absent: 1, excused: 0 },
    { day: 'Sun', present: 17, late: 1, absent: 2, excused: 1 }
  ];

  const features = [
    {
      title: 'Admin dashboard summary',
      icon: 'bi-speedometer2',
      text: 'See classes, students, teachers, parents, attendance activity, and key school totals from one admin view.'
    },
    {
      title: 'Teacher 7-day attendance graphs',
      icon: 'bi-bar-chart-line',
      text: 'Teachers can quickly review recent class attendance trends with color-coded present, absent, and excused activity.'
    },
    {
      title: 'Bulk student and parent upload',
      icon: 'bi-cloud-arrow-up',
      text: 'Admins can upload students and parent contacts into classes without entering every record one by one.'
    },
    {
      title: 'Reports for decisions',
      icon: 'bi-file-earmark-bar-graph',
      text: 'Review attendance, parent records, and financial reports in readable tables with search, print, share, and download options.'
    }
  ];

  const steps = [
    { icon: 'bi-upload', text: 'Admins can upload students and parents into classes instead of entering each one manually.' },
    { icon: 'bi-diagram-3', text: 'Build classes, assign teachers, and keep student lists organized.' },
    { icon: 'bi-check2-square', text: 'Mark attendance from admin or teacher dashboards.' },
    { icon: 'bi-clipboard-data', text: 'Review reports for attendance, parent records, and finances.' }
  ];

  return (
    <main className="home-page">
      <PageTitle
        title="MarkazSync"
        description="MarkazSync helps Islamic schools and markaz manage attendance, classes, students, parents, financials, and reports in one organized app."
        canonical="https://markazsync.com/home"
      />

      <section className="home-hero">
        <div className="home-hero__inner">
          <div className="home-hero__content">
            <div className="home-hero-card">
              <div className="home-eyebrow home-eyebrow--hero">MarkazSync School Management</div>
              <h1>Manage your markaz from one organized dashboard.</h1>
              <p>
                Track teachers, students, parents, attendance, classes, and reports with a system
                built for daily Islamic school operations.
              </p>
            </div>
            <div className="home-hero__actions">
              <span className="home-hero__actions-label">Ready to continue?</span>
              <Link className="home-btn home-btn--primary home-btn--signin" to="/">
                Staff Sign In
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>

          <div className="home-product-preview" aria-label="MarkazSync dashboard preview">
            <div className="home-preview-topbar">
              <div>
                <span className="home-preview-kicker">Live school overview</span>
                <strong>Admin Dashboard</strong>
              </div>
              <span className="home-preview-status">Synced</span>
            </div>

            <div className="home-admin-summary">
              {adminStats.map((item) => (
                <div className={`home-stat home-stat--${item.tone}`} key={item.label}>
                  <i className={`bi ${item.icon}`} aria-hidden="true" />
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>

            <div className="home-graph-card">
              <div className="home-graph-header">
                <div>
                  <span className="home-preview-kicker">Attendance overview</span>
                  <strong>7-Day Attendance</strong>
                  <small>Morning Weekend</small>
                </div>
                <div className="home-attendance-rate">
                  <strong>90%</strong>
                  <span>present rate</span>
                </div>
              </div>
              <div className="home-legend">
                <span className="home-legend__present">Present</span>
                <span className="home-legend__late">Late</span>
                <span className="home-legend__absent">Absent</span>
                <span className="home-legend__excused">Excused</span>
              </div>
              <div className="home-bars">
                {attendanceBars.map((day) => {
                  const total = day.present + day.late + day.absent + day.excused;
                  return (
                    <div className="home-bar" key={day.day}>
                      <div className="home-bar__stack" aria-hidden="true">
                        <span className="home-bar__present" style={{ height: `${(day.present / total) * 100}%` }} />
                        <span className="home-bar__late" style={{ height: `${(day.late / total) * 100}%` }} />
                        <span className="home-bar__absent" style={{ height: `${(day.absent / total) * 100}%` }} />
                        <span className="home-bar__excused" style={{ height: `${(day.excused / total) * 100}%` }} />
                      </div>
                      <small>{day.day}</small>
                    </div>
                  );
                })}
              </div>
              <div className="home-selected-day">
                <span>Selected day: Fri</span>
                <div className="home-legend">
                  <strong><span className="home-legend__present">Present: 18</span></strong>
                  <strong><span className="home-legend__late">Late: 2</span></strong>
                  <strong><span className="home-legend__absent">Absent: 2</span></strong>
                  <strong><span className="home-legend__excused">Excused: 1</span></strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="home-middle">
        <div className="home-middle__inner">
          <div className="home-middle__intro">
            <div>
              <span className="home-section-icon">
                <i className="bi bi-grid-1x2" aria-hidden="true" />
              </span>
              <div className="home-eyebrow">What It Does</div>
              <h2>One place for the work your markaz repeats every week.</h2>
            </div>
            <p>
              The app is built for admins and teachers who need a practical way to track students,
              mark attendance, review reports, and keep parent information accurate without using
              scattered spreadsheets.
            </p>
          </div>

          <div className="home-grid">
            {features.map((feature) => (
              <article className="home-feature" key={feature.title}>
                <div className="home-feature__top">
                  <span className="home-feature__icon">
                    <i className={`bi ${feature.icon}`} aria-hidden="true" />
                  </span>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section home-process">
        <div className="home-process__copy">
          <span className="home-section-icon">
            <i className="bi bi-signpost-split" aria-hidden="true" />
          </span>
          <div className="home-eyebrow">How It Works</div>
          <h2>Simple setup, daily use, clear reports.</h2>
          <p>
            MarkazSync separates access by role, so admins manage the school while teachers focus
            on their assigned classes.
          </p>
        </div>
        <ol className="home-steps">
          {steps.map((step, index) => (
            <li key={step.text}>
              <span>
                <i className={`bi ${step.icon}`} aria-hidden="true" />
              </span>
              <p>{step.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="home-section home-contact-band">
        <div>
          <span className="home-section-icon home-section-icon--light">
            <i className="bi bi-chat-dots" aria-hidden="true" />
          </span>
          <div className="home-eyebrow">Need More Information?</div>
          <h2>Contact us to learn how MarkazSync can support your markaz.</h2>
          <p>
            Send a message with your school name, what you want to manage, and the best way to
            reach you. We will follow up with more details.
          </p>
        </div>
        <Link className="home-btn home-btn--primary" to="/contact">Contact Us</Link>
      </section>

      <footer className="home-footer">
        <span>© {new Date().getFullYear()} MarkazSync. All rights reserved.</span>
        <span className="home-footer__links">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </span>
      </footer>
    </main>
  );
};

export default Home;
